/**
 * Inventory Dashboard Router
 * Role-based dashboard routing for Inventory Module
 * 
 * Routes users to the appropriate dashboard based on their role:
 * - QC Inspector / Quality roles → QC Inspector Dashboard
 * - Inventory Manager → Inventory Manager Dashboard (when available)
 * - Storekeeper / Default → Storekeeper Dashboard
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// Role to dashboard mapping
const ROLE_DASHBOARD_MAP = {
  // QC-focused roles go to QC Inspector Dashboard
  'quality_manager': '/inventory/qc-dashboard',
  'quality_engineer': '/inventory/qc-dashboard',
  'qc_inspector': '/inventory/qc-dashboard',
  
  // Inventory Manager gets the executive dashboard
  'inventory_manager': '/inventory/manager-dashboard',
  
  // Admin can access any - defaults to manager dashboard for executive view
  'admin': '/inventory/manager-dashboard',
  
  // Everyone else (storekeeper, operator, viewer) → Storekeeper Dashboard
  'default': '/inventory'
};

// Permission-based routing (more granular)
const PERMISSION_DASHBOARD_MAP = {
  'INV_QC_RELEASE': '/inventory/qc-dashboard',
  'INV_QC_REJECT': '/inventory/qc-dashboard',
  'INV_APPROVE_DISPOSITION': '/inventory/qc-dashboard',
};

/**
 * Hook to determine the best dashboard for the current user
 */
export const useInventoryDashboard = () => {
  const { user, hasRole, hasAnyRole } = useAuth();
  
  const getDashboardPath = () => {
    if (!user) return '/inventory';
    
    const userRole = user.role?.toLowerCase();
    
    // Check role-based mapping first
    if (userRole && ROLE_DASHBOARD_MAP[userRole]) {
      return ROLE_DASHBOARD_MAP[userRole];
    }
    
    // Check if user has QC-specific permissions
    const userPermissions = user.permissions || [];
    for (const [permission, path] of Object.entries(PERMISSION_DASHBOARD_MAP)) {
      if (userPermissions.includes(permission)) {
        return path;
      }
    }
    
    // Check by role patterns
    if (hasAnyRole(['quality_manager', 'quality_engineer'])) {
      return '/inventory/qc-dashboard';
    }
    
    // Default to storekeeper dashboard
    return '/inventory';
  };
  
  const isQCRole = () => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase();
    return ['quality_manager', 'quality_engineer', 'qc_inspector'].includes(userRole);
  };
  
  const isManagerRole = () => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase();
    return ['inventory_manager', 'admin'].includes(userRole);
  };
  
  return {
    getDashboardPath,
    isQCRole,
    isManagerRole,
    userRole: user?.role
  };
};

/**
 * Dashboard Selector Component
 * Shows available dashboards for roles with multiple access
 */
export const DashboardSelector = ({ onSelect, currentPath }) => {
  const { isQCRole, isManagerRole, userRole } = useInventoryDashboard();
  
  const dashboards = [
    {
      id: 'storekeeper',
      name: 'Storekeeper Dashboard',
      path: '/inventory',
      description: 'Day-to-day inventory operations',
      icon: '📦',
      roles: ['admin', 'inventory_manager', 'storekeeper', 'operator']
    },
    {
      id: 'qc-inspector',
      name: 'QC Inspector Dashboard',
      path: '/inventory/qc-dashboard',
      description: 'Quality inspection & release',
      icon: '🔍',
      roles: ['admin', 'inventory_manager', 'quality_manager', 'quality_engineer', 'qc_inspector']
    },
    {
      id: 'manager',
      name: 'Manager Dashboard',
      path: '/inventory/manager-dashboard',
      description: 'Executive KPIs & analytics',
      icon: '📊',
      roles: ['admin', 'inventory_manager']
    }
  ];
  
  // Filter dashboards based on user role
  const availableDashboards = dashboards.filter(d => {
    if (userRole === 'admin') return true;
    return d.roles.includes(userRole?.toLowerCase());
  });
  
  if (availableDashboards.length <= 1) {
    return null; // Don't show selector if only one option
  }
  
  return (
    <div className="flex gap-2">
      {availableDashboards.map(dashboard => (
        <button
          key={dashboard.id}
          onClick={() => onSelect(dashboard.path)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            currentPath === dashboard.path
              ? 'bg-[#101957] text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={dashboard.description}
        >
          <span className="mr-1.5">{dashboard.icon}</span>
          {dashboard.name.replace(' Dashboard', '')}
        </button>
      ))}
    </div>
  );
};

/**
 * Auto-redirect wrapper for inventory landing
 * Redirects to the appropriate dashboard based on role
 */
export const InventoryDashboardRedirect = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { getDashboardPath } = useInventoryDashboard();
  const [shouldRender, setShouldRender] = useState(false);
  
  useEffect(() => {
    // Only redirect if we're on the exact /inventory path
    if (location.pathname === '/inventory') {
      const targetPath = getDashboardPath();
      
      // If user should go to QC dashboard, redirect
      if (targetPath !== '/inventory') {
        navigate(targetPath, { replace: true });
        return;
      }
    }
    
    setShouldRender(true);
  }, [location.pathname, user, getDashboardPath, navigate]);
  
  if (!shouldRender && location.pathname === '/inventory') {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#101957] mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  return children;
};

export default useInventoryDashboard;
